// =====================================================================================
// INVRT Radar - web console - HIGH-INTENT seam types (Story N3)
//
// The browser reads HIGH-INTENT data ONLY through the app's own api base under /v1/radar/*
// (the N2 proxy seam; canonical route table pinned by the orchestrator). These types and
// the parsers below describe the five canonical reads and nothing
// more; a response that is structurally nonsense fails closed as an ApiError instead of
// rendering half a page. Nothing here carries, names or implies an upstream origin, key or
// assertion: the proxy is the whole world.
// =====================================================================================

import { ApiError } from "@/lib/api";

export interface RadarCampaign {
  id: string;
  code: string;
  name: string;
  status: string;
  counters: Record<string, number | null>;
  last_run_at: string | null;
}

export interface RadarCampaignsPage {
  correlation_id: string;
  campaigns: RadarCampaign[];
  next_cursor: string | null;
}

export interface RadarLead {
  id: string;
  company: string | null;
  contact_name: string | null;
  role: string | null;
  state: string | null;
  industry: string | null;
  tag: string | null;
  intent_band: string | null;
  score: number | null;
  quality_score: number;
  campaign_id: string | null;
}

export interface RadarLeadsPage {
  correlation_id: string;
  leads: RadarLead[];
  next_cursor: string | null;
}

export interface RadarRun {
  id: string;
  trigger: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  result_summary: Record<string, number | string | null>;
  created_at: string;
  provider: string;
  model: string;
  prompt_version: string;
  degraded: boolean;
  cost_usd: string | null;
}

export interface RadarRunsPage {
  correlation_id: string;
  runs: RadarRun[];
  summary: { total_runs: number; by_status: Record<string, number> };
}

export interface RadarLedgerRow {
  provider_uuid: string;
  radar_campaign_id: string | null;
  high_intent_campaign_key: string;
  delivered_at: string;
  delivery_status: string;
}

export interface RadarLedgerPage {
  correlation_id: string;
  ledger: RadarLedgerRow[];
  next_cursor: string | null;
}

export interface RadarStatsCampaign {
  radar_campaign_id: string | null;
  high_intent_campaign_key: string;
  display_name: string;
  active: boolean;
  batches: number;
  parsed_records: number;
  new_leads: number;
  re_intent_events: number;
  high_intent_count: number;
  distinct_persons: number;
  first_window: string | null;
  last_window: string | null;
}

export interface RadarSpendDay {
  call_date: string;
  calls: number;
  estimated_cost_usd: string;
  actual_cost_usd: string | null;
}

export interface RadarProcessing {
  batches: number;
  parsed_records: number;
  dispositions: Record<string, number>;
  rejection_reasons: Array<{ reason: string; count: number }>;
  tag_counts: Record<string, number>;
  lead_tag_counts: Record<string, number>;
  re_intent_counted: number;
  re_intent_not_counted: number;
}

export interface RadarStats {
  correlation_id: string | null;
  campaign: RadarStatsCampaign | null;
  spend_by_day: RadarSpendDay[];
  /** How the data was processed: ledger dispositions, refusal WHYs, tag
   * spread, re-intent by the counted law. Null when an older engine has not
   * answered with the block yet, so the panel renders nothing rather than
   * half of it. */
  processing: RadarProcessing | null;
}

function invalidContract(message: string): ApiError {
  return new ApiError({
    token: "internal_error",
    message,
    httpStatus: 502,
    correlationId: null,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function requiredString(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) throw invalidContract(BAD_CONTRACT);
  return value;
}

function nullableString(value: unknown): string | null {
  if (value === null) return null;
  return requiredString(value);
}

function requiredCount(value: unknown): number {
  const count = optionalCount(value);
  if (count === null) throw invalidContract(BAD_CONTRACT);
  return count;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function requiredUuid(value: unknown): string {
  const id = requiredString(value);
  if (!UUID_PATTERN.test(id)) throw invalidContract(BAD_CONTRACT);
  return id;
}

function nullableUuid(value: unknown): string | null {
  return value === null ? null : requiredUuid(value);
}

const BAD_CONTRACT = "The HIGH-INTENT response did not match the RADAR proxy contract.";

export function parseCampaignsPage(value: unknown): RadarCampaignsPage {
  if (!isRecord(value) || !Array.isArray(value.campaigns)) throw invalidContract(BAD_CONTRACT);
  const correlation_id = requiredString(value.correlation_id);
  const campaigns: RadarCampaign[] = value.campaigns.map((row) => {
    if (!isRecord(row) || typeof row.active !== "boolean") throw invalidContract(BAD_CONTRACT);
    // An org-only binding has no UUID for the scoped runs/stats routes.
    // Refuse it rather than minting a route from the campaign key.
    return {
      id: requiredUuid(row.radar_campaign_id),
      code: requiredString(row.high_intent_campaign_key),
      name: requiredString(row.display_name),
      status: row.active ? "active" : "paused",
      counters: {
        leads: requiredCount(row.new_leads),
        high_intent: requiredCount(row.high_intent_count),
      },
      last_run_at: null,
    };
  });
  return { correlation_id, campaigns, next_cursor: null };
}

export function parseLeadsPage(value: unknown): RadarLeadsPage {
  if (!isRecord(value) || !Array.isArray(value.leads)) throw invalidContract(BAD_CONTRACT);
  const correlation_id = requiredString(value.correlation_id);
  const next_cursor = nullableString(value.next_cursor);
  const leads: RadarLead[] = value.leads.map((row) => {
    if (!isRecord(row) || typeof row.high_intent !== "boolean" ||
      (row.lead_tag !== "warm" && row.lead_tag !== "standard") ||
      (row.tag !== "A" && row.tag !== "B" && row.tag !== "C")
    ) throw invalidContract(BAD_CONTRACT);
    if (row.ai_intent_score !== null && optionalNumber(row.ai_intent_score) === null) {
      throw invalidContract(BAD_CONTRACT);
    }
    return {
      id: requiredUuid(row.provider_uuid),
      company: nullableString(row.company_name),
      contact_name: null,
      role: nullableString(row.seniority_level),
      state: nullableString(row.personal_state),
      industry: nullableString(row.company_industry),
      tag: row.tag,
      intent_band: row.high_intent ? "high" : row.lead_tag,
      // The view's Score is the engine's AI intent score, not quality_score.
      score: optionalNumber(row.ai_intent_score),
      quality_score: requiredCount(row.quality_score),
      campaign_id: nullableUuid(row.radar_campaign_id),
    };
  });
  return { correlation_id, leads, next_cursor };
}

export function parseRunsPage(value: unknown): RadarRunsPage {
  if (!isRecord(value) || !Array.isArray(value.runs) || !isRecord(value.summary)) {
    throw invalidContract(BAD_CONTRACT);
  }
  const correlation_id = requiredString(value.correlation_id);
  const total_runs = requiredCount(value.summary.total_runs);
  const by_status = countsOf(value.summary.by_status);
  if (by_status === null) throw invalidContract(BAD_CONTRACT);
  const runs: RadarRun[] = value.runs.map((row) => {
    if (!isRecord(row) || typeof row.degraded !== "boolean") throw invalidContract(BAD_CONTRACT);
    return {
      id: requiredUuid(row.batch_id),
      trigger: requiredString(row.analysis_type),
      status: requiredString(row.status),
      started_at: null,
      finished_at: null,
      result_summary: {},
      created_at: requiredString(row.created_at),
      provider: requiredString(row.provider),
      model: requiredString(row.model),
      prompt_version: requiredString(row.prompt_version),
      degraded: row.degraded,
      cost_usd: nullableString(row.cost_usd),
    };
  });
  return { correlation_id, runs, summary: { total_runs, by_status } };
}

export function parseLedgerPage(value: unknown): RadarLedgerPage {
  if (!isRecord(value) || !Array.isArray(value.ledger)) throw invalidContract(BAD_CONTRACT);
  const correlation_id = requiredString(value.correlation_id);
  const next_cursor = nullableString(value.next_cursor);
  const ledger: RadarLedgerRow[] = value.ledger.map((row) => {
    if (!isRecord(row)) throw invalidContract(BAD_CONTRACT);
    return {
      provider_uuid: requiredUuid(row.provider_uuid),
      radar_campaign_id: nullableUuid(row.radar_campaign_id),
      high_intent_campaign_key: requiredString(row.high_intent_campaign_key),
      delivered_at: requiredString(row.delivered_at),
      delivery_status: requiredString(row.delivery_status),
    };
  });
  return { correlation_id, ledger, next_cursor };
}

/** A finite count, or null. Counts arrive from deterministic SQL. */
function optionalCount(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;
}

/** A flat record of integer counts, or null when absent or malformed. */
function countsOf(value: unknown): Record<string, number> | null {
  if (value === undefined || value === null) return null;
  if (!isRecord(value)) throw invalidContract(BAD_CONTRACT);
  const out: Record<string, number> = {};
  for (const [key, count] of Object.entries(value)) {
    const n = optionalCount(count);
    if (n === null) throw invalidContract(BAD_CONTRACT);
    out[key] = n;
  }
  return out;
}

export function parseStats(value: unknown): RadarStats {
  if (!isRecord(value)) throw invalidContract(BAD_CONTRACT);
  // The engine's real shape: campaign entry + spend days of four fields +
  // the processing block. (The pre-integration parser read a shape the
  // engine never sent, so spend could only ever render empty.)
  const spendByDay: RadarSpendDay[] = [];
  if (value.spend_by_day !== undefined && value.spend_by_day !== null) {
    if (!Array.isArray(value.spend_by_day)) throw invalidContract(BAD_CONTRACT);
    for (const row of value.spend_by_day) {
      if (!isRecord(row) || typeof row.call_date !== "string") {
        throw invalidContract(BAD_CONTRACT);
      }
      const calls = optionalCount(row.calls);
      if (calls === null || typeof row.estimated_cost_usd !== "string") {
        throw invalidContract(BAD_CONTRACT);
      }
      spendByDay.push({
        call_date: row.call_date,
        calls,
        estimated_cost_usd: row.estimated_cost_usd,
        actual_cost_usd: optionalString(row.actual_cost_usd),
      });
    }
  }
  let campaign: RadarStatsCampaign | null = null;
  if (value.campaign !== undefined && value.campaign !== null) {
    if (!isRecord(value.campaign)) throw invalidContract(BAD_CONTRACT);
    const entry = value.campaign;
    if (
      typeof entry.high_intent_campaign_key !== "string" ||
      typeof entry.display_name !== "string" ||
      typeof entry.active !== "boolean"
    ) {
      throw invalidContract(BAD_CONTRACT);
    }
    const batches = optionalCount(entry.batches);
    const parsed = optionalCount(entry.parsed_records);
    const newLeads = optionalCount(entry.new_leads);
    const reIntent = optionalCount(entry.re_intent_events);
    const highIntent = optionalCount(entry.high_intent_count);
    const distinct = optionalCount(entry.distinct_persons);
    if (
      batches === null || parsed === null || newLeads === null ||
      reIntent === null || highIntent === null || distinct === null
    ) {
      throw invalidContract(BAD_CONTRACT);
    }
    campaign = {
      radar_campaign_id: optionalString(entry.radar_campaign_id),
      high_intent_campaign_key: entry.high_intent_campaign_key,
      display_name: entry.display_name,
      active: entry.active,
      batches,
      parsed_records: parsed,
      new_leads: newLeads,
      re_intent_events: reIntent,
      high_intent_count: highIntent,
      distinct_persons: distinct,
      first_window: optionalString(entry.first_window),
      last_window: optionalString(entry.last_window),
    };
  }
  let processing: RadarProcessing | null = null;
  if (value.processing !== undefined && value.processing !== null) {
    if (!isRecord(value.processing)) throw invalidContract(BAD_CONTRACT);
    const block = value.processing;
    const batches = optionalCount(block.batches);
    const parsed = optionalCount(block.parsed_records);
    const counted = optionalCount(block.re_intent_counted);
    const notCounted = optionalCount(block.re_intent_not_counted);
    if (batches === null || parsed === null || counted === null || notCounted === null) {
      throw invalidContract(BAD_CONTRACT);
    }
    if (!Array.isArray(block.rejection_reasons)) throw invalidContract(BAD_CONTRACT);
    const rejectionReasons = block.rejection_reasons.map((row) => {
      if (!isRecord(row) || typeof row.reason !== "string") throw invalidContract(BAD_CONTRACT);
      const count = optionalCount(row.count);
      if (count === null) throw invalidContract(BAD_CONTRACT);
      return { reason: row.reason, count };
    });
    processing = {
      batches,
      parsed_records: parsed,
      dispositions: countsOf(block.dispositions) ?? {},
      rejection_reasons: rejectionReasons,
      tag_counts: countsOf(block.tag_counts) ?? {},
      lead_tag_counts: countsOf(block.lead_tag_counts) ?? {},
      re_intent_counted: counted,
      re_intent_not_counted: notCounted,
    };
  }
  return {
    correlation_id: optionalString(value.correlation_id),
    campaign,
    spend_by_day: spendByDay,
    processing,
  };
}
